import {
    useEventCountdown,
    useEventMode,
    useEventStartCountdown,
} from "@/context/EventModeContext";
import useLocale from "@/hooks/useLocale";
import { formatCountdown, formatEventDate } from "@/lib/events";
import { readStorage, writeStorage } from "@/lib/storage";
import { ClockState } from "@openapi";
import {
    ClockCircleOutlined,
    LogoutOutlined,
    ShrinkOutlined,
    UnorderedListOutlined,
    TrophyOutlined,
} from "@ant-design/icons";
import { Alert, App, Button, Space, Spin, Tooltip, Typography } from "antd";
import type { AlertProps } from "antd";
import { useCallback, useState } from "react";
import EventMiniBadge from "./EventMiniBadge";
import Link from "next/link";
import { useTranslation } from "react-i18next";

// Under this many seconds the running banner turns into a warning.
const LAST_MINUTES_SECONDS = 5 * 60;

const MINIMIZED_KEY = "eventBannerMinimized";

const readMinimized = (): boolean => readStorage(MINIMIZED_KEY) === "true";

// Storage unavailable: the choice lasts until reload.
const writeMinimized = (minimized: boolean) =>
    writeStorage(MINIMIZED_KEY, String(minimized));

// Tells the student they are in event mode: event, team, clock, and a way out.
export default function EventBanner() {
    const { t } = useTranslation();
    const { modal } = App.useApp();
    const { eventId, event, loading, error, clockState, leaveEvent, refresh } =
        useEventMode();
    const secondsRemaining = useEventCountdown();
    const secondsToStart = useEventStartCountdown();
    const locale = useLocale();
    const [minimized, setMinimized] = useState(readMinimized);

    const toggleMinimized = useCallback((value: boolean) => {
        setMinimized(value);
        writeMinimized(value);
    }, []);

    const confirmLeave = useCallback(() => {
        modal.confirm({
            title: t("events.mode.leaveConfirmTitle"),
            content: t("events.mode.leaveConfirmContent"),
            okText: t("events.mode.leave"),
            cancelText: t("button.cancel"),
            onOk: leaveEvent,
        });
    }, [leaveEvent, modal, t]);

    if (!eventId) return null;

    const leaveButton = (
        <Button size="small" icon={<LogoutOutlined />} onClick={confirmLeave}>
            {t("events.mode.leave")}
        </Button>
    );

    if (!event) {
        return (
            <Alert
                banner
                type={error ? "error" : "info"}
                icon={loading ? <Spin size="small" /> : <TrophyOutlined />}
                showIcon
                title={error ?? t("events.mode.loading")}
                action={
                    <Space>
                        {error && (
                            <Button size="small" onClick={() => void refresh()}>
                                {t("events.retry")}
                            </Button>
                        )}
                        {leaveButton}
                    </Space>
                }
            />
        );
    }

    const members = (event.competitor.members ?? [])
        .map((m) => m.user_name)
        .filter(Boolean)
        .join(", ");
    const team =
        event.competitor.name || members || t("events.mode.unnamedTeam");
    const organizationName =
        event.organization?.organization_name ??
        event.competitor.organization_name;

    let type: NonNullable<AlertProps["type"]> = "info";
    let status: React.ReactNode;
    // Short form of the status for the minimised badge.
    let shortStatus: string;
    if (clockState === ClockState.Running && secondsRemaining != null) {
        type = secondsRemaining <= LAST_MINUTES_SECONDS ? "warning" : "success";
        shortStatus = formatCountdown(secondsRemaining);
        status = (
            <Space size={6}>
                <ClockCircleOutlined />
                <Typography.Text
                    strong
                    style={{
                        fontVariantNumeric: "tabular-nums",
                        fontSize: 16,
                    }}
                >
                    {formatCountdown(secondsRemaining)}
                </Typography.Text>
                <Typography.Text type="secondary">
                    {t("events.mode.timeLeft")}
                </Typography.Text>
            </Space>
        );
    } else if (clockState === ClockState.Finished) {
        type = "error";
        shortStatus = t("events.clock.finished");
        status = (
            <Typography.Text strong>
                {t("events.mode.finished")}
            </Typography.Text>
        );
    } else if (event.organization) {
        shortStatus = t("events.clock.waiting");
        status = (
            <Typography.Text>
                {t("events.mode.waitingForOrganization", {
                    organization:
                        organizationName ?? t("events.mode.yourOrganization"),
                })}
            </Typography.Text>
        );
    } else {
        shortStatus =
            secondsToStart !== null
                ? t("events.mode.startsIn", {
                      time: formatCountdown(secondsToStart),
                  })
                : t("events.clock.waiting");
        status = (
            <Typography.Text>
                {t("events.mode.waitingForWindow", {
                    start: formatEventDate(event.event.available_start, locale),
                })}
                {secondsToStart !== null && (
                    <>
                        {" "}
                        <Typography.Text
                            strong
                            style={{ fontVariantNumeric: "tabular-nums" }}
                        >
                            {t("events.mode.startsIn", {
                                time: formatCountdown(secondsToStart),
                            })}
                        </Typography.Text>
                    </>
                )}
            </Typography.Text>
        );
    }

    if (minimized) {
        return (
            <EventMiniBadge
                type={type}
                status={shortStatus}
                eventName={event.event.name}
                onExpand={() => toggleMinimized(false)}
            />
        );
    }

    return (
        <Alert
            banner
            type={type}
            icon={<TrophyOutlined />}
            showIcon
            title={
                <Space size="middle" wrap>
                    <Typography.Text strong>
                        {t("events.mode.label")}: {event.event.name}
                    </Typography.Text>
                    <Typography.Text>
                        {t("events.mode.team")}: {team}
                        {members && event.competitor.name
                            ? ` (${members})`
                            : ""}
                    </Typography.Text>
                    {event.competitor.join_code && (
                        <Typography.Text type="secondary">
                            {t("events.teams.codeShort")}:{" "}
                            <Typography.Text code copyable>
                                {event.competitor.join_code}
                            </Typography.Text>
                        </Typography.Text>
                    )}
                    {status}
                </Space>
            }
            action={
                <Space>
                    <Link href={`/${locale}/events/${event.event.id}`}>
                        <Button size="small" icon={<UnorderedListOutlined />}>
                            {t("events.details.problems")}
                        </Button>
                    </Link>
                    {leaveButton}
                    <Tooltip title={t("events.mode.minimize")}>
                        <Button
                            size="small"
                            type="text"
                            icon={<ShrinkOutlined />}
                            aria-label={t("events.mode.minimize")}
                            onClick={() => toggleMinimized(true)}
                        />
                    </Tooltip>
                </Space>
            }
        />
    );
}
